import type { MonitorAndProtectParams } from "app/pages/digital-marketing/monitor-and-protect/app/keywords/routes.types";
import type { RedirectHandler } from "app/pages/digital-marketing/monitor-and-protect/shared/redirect-handlers/types";

/**
 * Redirect handler that filters URL parameters to only include those declared in the target state.
 *
 * This prevents invalid or undeclared parameters from persisting in the URL during navigation,
 * which could cause routing issues or expose unwanted state information.
 *
 * @param transition - The UI-Router transition object containing source/target states and params
 * @returns Object with cleaned params if redirect needed, undefined if no action required
 */
// eslint-disable-next-line @typescript-eslint/require-await
export const allowOnlyDeclaredParams: RedirectHandler = async (transition) => {
    // Get the target state we're navigating to
    const toState = transition.to();
    // Extract current URL parameters from the transition
    const currentParams = transition.params() as MonitorAndProtectParams;

    // Remove only nullish values (null, undefined) from parameters
    // This prevents empty parameters from being considered for validation while preserving falsy values like 0, false, ""
    const paramsWithoutNullishValues = Object.entries(currentParams).reduce((acc, [param, value]) => {
        if (value !== null && value !== undefined) {
            acc[param] = value;
        }
        return acc;
    }, {} as MonitorAndProtectParams);

    // Get the keys of parameters declared in the target state
    // These are the "allowed" parameter names for this route
    const declaredParamKeys = new Set(Object.keys(toState.params ?? {}));

    // Check if any current parameters are not declared in the target state
    const hasUndeclaredParams = Object.keys(paramsWithoutNullishValues).some((param) => !declaredParamKeys.has(param));

    // If undeclared params exist, filter them out and return cleaned params for redirect
    if (hasUndeclaredParams) {
        const cleanedParams = Object.keys(paramsWithoutNullishValues).reduce((acc, param) => {
            if (declaredParamKeys.has(param)) {
                // Use original currentParams to preserve original values (including nullish ones)
                const paramKey = param as keyof MonitorAndProtectParams;
                acc[paramKey] = currentParams[paramKey] ?? null;
            }
            return acc;
        }, {} as MonitorAndProtectParams);

        // Return cleaned params to trigger a redirect with valid parameters only
        return {
            params: cleanedParams,
        };
    }

    // No undeclared params found, allow navigation to proceed normally
    return void 0;
};
